import { FormEvent, useEffect, useState } from 'react'
import { Alert, Box, Chip, Link, TextField, Typography } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { useSearchParams } from 'react-router-dom'
import PageHero from '../shared/PageHero'
import Section from '../shared/Section'
import TvButton from '../shared/TvButton'
import { COLORS, SITE } from '../../constants'
import { contactTopics } from '../../utils/siteContent'
import usePageMeta from '../../utils/usePageMeta'

// TODO(Thomas): set REACT_APP_CONTACT_ENDPOINT at build time (e.g. an API Gateway + Lambda that sends via SES)
// to receive submissions directly. Without it, the form opens the visitor's email app with everything prefilled.
const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT

type Status = 'idle' | 'sending' | 'sent' | 'mailto' | 'error'

export function buildMailto(form: { name: string, email: string, company: string, topic: string, message: string }) {
    const subject = `${form.topic || 'Project inquiry'} from ${form.name}`
    const details = [
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        form.company && `Company: ${form.company}`,
        form.topic && `Topic: ${form.topic}`,
    ].filter(Boolean)
    const body = `${form.message}\n\n---\n${details.join('\n')}`
    return `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

function Contact() {
    usePageMeta({
        title: 'Contact: Start a Project',
        description: 'Tell me what you’re trying to build, automate or fix. Freelance software engineering, automation, AWS consulting and mentorship from Indianapolis.',
        path: '/contact',
    })

    const [params] = useSearchParams()
    const topicParam = contactTopics.includes(params.get('topic') ?? '') ? params.get('topic')! : ''
    const [form, setForm] = useState({ name: '', email: '', company: '', topic: topicParam, message: '', website: '' })
    const [status, setStatus] = useState<Status>('idle')

    // Follow ?topic= if it changes while this page is already open (e.g. a service link clicked from here)
    useEffect(() => {
        if (topicParam) setForm((f) => ({ ...f, topic: topicParam }))
    }, [topicParam])

    const update = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [field]: e.target.value })

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (form.website) return // honeypot

        if (!CONTACT_ENDPOINT) {
            window.location.href = buildMailto(form)
            setStatus('mailto')
            return
        }

        setStatus('sending')
        try {
            const { website, ...payload } = form
            const res = await fetch(CONTACT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            })
            if (!res.ok) throw new Error(`Request failed: ${res.status}`)
            setStatus('sent')
        } catch {
            setStatus('error')
        }
    }

    return (
        <>
            <PageHero
                eyebrow="Contact"
                title="Tell me what you’re trying to build"
                intro="Or what’s annoying, expensive, repetitive, or broken. A few sentences is plenty. You don’t need a spec or a budget figured out yet."
            />

            <Section tone="light">
                <Box sx={{ display: 'grid', gap: { xs: 5, md: 8 }, gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' }, alignItems: 'start' }}>
                    <Box sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}>
                        {status === 'sent' ? (
                            <Box role="status">
                                <Typography variant="h3">Thanks, message received.</Typography>
                                <Typography sx={{ mt: 1.5, color: 'text.secondary' }}>I’ll get back to you at {form.email} soon.</Typography>
                            </Box>
                        ) : (
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2.5 }}>
                                <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                    <TextField label="Name" name="name" required value={form.name} onChange={update('name')} autoComplete="name" />
                                    <TextField label="Email" name="email" type="email" required value={form.email} onChange={update('email')} autoComplete="email" />
                                </Box>
                                <TextField label="Company (optional)" name="company" value={form.company} onChange={update('company')} autoComplete="organization" />

                                <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
                                    <Typography component="legend" sx={{ fontWeight: 600, mb: 1.25 }}>What do you need help with?</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {contactTopics.map((t) => {
                                            const selected = form.topic === t
                                            return (
                                                <Chip
                                                    key={t}
                                                    label={t}
                                                    clickable
                                                    aria-pressed={selected}
                                                    onClick={() => setForm({ ...form, topic: selected ? '' : t })}
                                                    sx={{
                                                        height: 38, fontSize: '0.95rem', fontWeight: 500, borderRadius: 999,
                                                        backgroundColor: selected ? COLORS.black : COLORS.light,
                                                        color: selected ? COLORS.white : COLORS.black,
                                                        border: `1px solid ${selected ? COLORS.black : COLORS.border}`,
                                                        '&:hover': { backgroundColor: selected ? COLORS.blackSecondary : '#EAEEF2' },
                                                    }}
                                                />
                                            )
                                        })}
                                    </Box>
                                </Box>

                                <TextField
                                    label="Brief description"
                                    name="message"
                                    required
                                    multiline
                                    minRows={5}
                                    value={form.message}
                                    onChange={update('message')}
                                    placeholder="What’s the problem, and what would “fixed” look like?"
                                />

                                {/* Honeypot: hidden from people, bots tend to fill it in */}
                                <Box sx={{ position: 'absolute', left: '-10000px' }} aria-hidden="true">
                                    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={update('website')} />
                                </Box>

                                {status === 'error' && (
                                    <Alert severity="error">
                                        Something went wrong sending that. Please email me directly at <Link href={buildMailto(form)}>{SITE.email}</Link>.
                                    </Alert>
                                )}
                                {status === 'mailto' && (
                                    <Alert severity="info">
                                        Your email app should have opened with your message ready to send. If it didn’t, email me at <Link href={`mailto:${SITE.email}`}>{SITE.email}</Link>.
                                    </Alert>
                                )}

                                <Box>
                                    <TvButton text={status === 'sending' ? 'Sending…' : 'Send Message'} type="submit" disabled={status === 'sending'} icon={<SendIcon fontSize="small" />} size="large" />
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'grid', gap: 4 }}>
                        <Box>
                            <Typography variant="h4" component="h2">What happens next</Typography>
                            <Box component="ol" sx={{ mt: 1.5, pl: 2.5, display: 'grid', gap: 1, color: 'text.secondary' }}>
                                <Typography component="li">I read your message and reply personally.</Typography>
                                <Typography component="li">If it looks like a fit, we set up a short call to talk it through.</Typography>
                                <Typography component="li">You get a plain-language plan before any work starts.</Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="h4" component="h2">Prefer email?</Typography>
                            <Link href={`mailto:${SITE.email}`} sx={{ mt: 1, display: 'inline-block', color: COLORS.accentOnLight, fontWeight: 600, wordBreak: 'break-all' }}>{SITE.email}</Link>
                        </Box>
                        <Box>
                            <Typography variant="h4" component="h2">Based in</Typography>
                            <Typography sx={{ mt: 1, color: 'text.secondary' }}>{SITE.location}. Happy to work remotely with teams anywhere.</Typography>
                        </Box>
                    </Box>
                </Box>
            </Section>
        </>
    )
}

export default Contact
